import type {
  CarState,
  Control,
  DriverData,
  NeuroDriveData,
  ParticipantSelection,
  QuickRaceSelection,
  RaceResult,
  SensorObstacle,
  SensorReading,
  Vec2,
  VehicleData,
} from "../types";
import { collisionRadius, resolveVehicleCollision } from "./collisions";
import { bridgeHeightAt, bridgeRailHalfWidthAt } from "./bridge";
import { forward, type NetworkActivations } from "./network";
import { add, clip, cloneVec, dot, headingVector, scale, sub } from "./math";
import { resolveWallContact, updateCar } from "./physics";
import { ProgressEvaluator } from "./progress";
import { RecoveryTracker } from "./recovery";
import { RabbitWorld } from "./rabbits";
import { encodeObservation, readSensors } from "./sensors";
import { TrackRuntime } from "./track";
import { StuntController, type StuntEvent } from "./stunts";

export interface RaceParticipant {
  selection: ParticipantSelection;
  driver: DriverData | null;
  vehicle: VehicleData;
  car: CarState;
  progress: ProgressEvaluator;
  status: "RACING" | "FINISHED" | "DNF";
  finishTime: number | null;
  lastLapStarted: number;
  fastestLap: number | null;
  observedLaps: number;
  completedLaps: number;
  lastRaceProgress: number;
  lastRaceSegmentIndex: number;
  trackCollisions: number;
  opponentCollisions: number;
  rabbitIncidents: number;
  readings: SensorReading[];
  observation: number[];
  activations: NetworkActivations | null;
  control: Control;
  recovery: RecoveryTracker;
  wallContactActive: boolean;
  terminationReason: string | null;
  stuntController: StuntController;
  stuntEvents: StuntEvent[];
  rampEntries: number;
  airbornePhases: number;
  cleanLandings: number;
  obstacleClears: number;
  landingCollisions: number;
  tornadoEntries: number;
  tornadoCompletions: number;
  tornadoMisfires: number;
  totalAirTime: number;
  totalJumpDistance: number;
  firstLandingProgress: number | null;
  postLandingProgress: number;
  clearedObstacleIds: Set<string>;
  lastLandingStep: number;
  automaticResets: number;
}

export interface ImpactEvent {
  position: [number, number];
  participantIds: [string, string];
  elapsedSeconds: number;
}

const idleControl: Control = { steering: 0, throttleBrake: 0 };
export const PODIUM_GRACE_SECONDS = 5;
const finishCoastControl: Control = { steering: 0, throttleBrake: -0.38 };

export function rankRaceResults(results: RaceResult[]): RaceResult[] {
  const tiers = { FINISHED: 0, RACING: 1, DNF: 2 } as const;
  return [...results].sort((first, second) => {
    const tier = tiers[first.status] - tiers[second.status];
    if (tier !== 0) return tier;
    if (first.status === "FINISHED" && second.status === "FINISHED") {
      return (first.finishTime ?? Infinity) - (second.finishTime ?? Infinity);
    }
    if (first.progressLaps !== second.progressLaps) return second.progressLaps - first.progressLaps;
    return first.displayName.localeCompare(second.displayName);
  });
}

export class QuickRaceEngine {
  readonly data: NeuroDriveData;
  readonly selection: QuickRaceSelection;
  readonly track: TrackRuntime;
  readonly participants: RaceParticipant[];
  readonly rabbitWorld: RabbitWorld;
  readonly impactEvents: ImpactEvent[] = [];
  steps = 0;
  private podiumLockedAt: number | null = null;
  private activeVehicleContacts = new Set<string>();
  private activeRabbitContacts = new Set<string>();

  constructor(data: NeuroDriveData, selection: QuickRaceSelection) {
    this.data = data;
    this.selection = selection;
    const trackData = data.tracks.find((track) => track.key === selection.trackKey);
    if (!trackData) throw new Error(`Unknown track ${selection.trackKey}.`);
    this.track = new TrackRuntime(trackData);
    const preset = data.race.rabbit_presets.find(
      (item) => item.track === selection.trackKey && item.count === selection.rabbitCount && item.seed === selection.raceSeed,
    );
    if (!preset) throw new Error("No deterministic rabbit preset matches this race config.");
    this.rabbitWorld = new RabbitWorld(this.track, preset);
    this.participants = selection.participants.map((item, index) => this.createParticipant(item, index));
  }

  private createParticipant(selection: ParticipantSelection, index: number): RaceParticipant {
    const driver = selection.driverId === "human" ? null : this.data.drivers.find((item) => item.id === selection.driverId) ?? null;
    if (selection.driverId !== "human" && !driver) throw new Error(`Unknown driver ${selection.driverId}.`);
    const vehicle = this.data.vehicles.find((item) => item.key === selection.vehicleId);
    if (!vehicle) throw new Error(`Unknown vehicle ${selection.vehicleId}.`);
    const grid = this.data.race.start_grid;
    const row = Math.floor(index / grid.columns);
    const column = index % grid.columns;
    const start = this.track.pointAt(-(grid.line_offset + row * grid.row_spacing));
    const rowCount = Math.min(grid.columns, this.selection.participants.length - row * grid.columns);
    const lateral = (column - (rowCount - 1) / 2) * grid.lateral_spacing;
    const position: Vec2 = [
      start.point[0] - Math.sin(start.heading) * lateral,
      start.point[1] + Math.cos(start.heading) * lateral,
    ];
    const startProjection = this.track.project(position);
    const car: CarState = {
      position, heading: start.heading, speed: 0, collided: false,
      z: 0, verticalVelocity: 0, airborne: false, pitch: 0,
      stuntSegmentId: null, stuntProgress: 0, stuntSpeed: 0,
      airborneTime: 0, landingGripSeconds: 0,
      waterGripSeconds: 0, waterGripFactor: 1, onWater: false, onBridge: false,
    };
    return {
      selection,
      driver,
      vehicle,
      car,
      progress: new ProgressEvaluator(this.track, car.position),
      status: "RACING",
      finishTime: null,
      lastLapStarted: 0,
      fastestLap: null,
      observedLaps: 0,
      completedLaps: 0,
      lastRaceProgress: startProjection.progress,
      lastRaceSegmentIndex: startProjection.segmentIndex,
      trackCollisions: 0,
      opponentCollisions: 0,
      rabbitIncidents: 0,
      readings: [],
      observation: [],
      activations: null,
      control: idleControl,
      recovery: new RecoveryTracker(this.data.game_semantics.fixed_dt),
      wallContactActive: false,
      terminationReason: null,
      stuntController: new StuntController(this.data.game_semantics.stunts, vehicle.physics),
      stuntEvents: [], rampEntries: 0, airbornePhases: 0, cleanLandings: 0,
      obstacleClears: 0, landingCollisions: 0, tornadoEntries: 0,
      tornadoCompletions: 0, tornadoMisfires: 0, totalAirTime: 0,
      totalJumpDistance: 0, firstLandingProgress: null, postLandingProgress: 0,
      clearedObstacleIds: new Set<string>(), lastLandingStep: -10000,
      automaticResets: 0,
    };
  }

  get elapsedSeconds(): number {
    return this.steps * this.data.game_semantics.fixed_dt;
  }

  get finished(): boolean {
    return this.participants.every((participant) => participant.status !== "RACING");
  }

  get recentImpactEvents(): ImpactEvent[] {
    return this.impactEvents.filter((event) => this.elapsedSeconds - event.elapsedSeconds <= 0.28);
  }

  get podiumGraceRemaining(): number | null {
    if (this.podiumLockedAt === null) return null;
    return Math.max(0, PODIUM_GRACE_SECONDS - (this.elapsedSeconds - this.podiumLockedAt));
  }

  step(humanControl: Control = idleControl): void {
    if (this.finished) return;
    const dt = this.data.game_semantics.fixed_dt;
    this.updateFinishedCoast(dt);
    this.rabbitWorld.step(dt);
    const rabbitObstacles = this.rabbitWorld.obstacles();
    for (const participant of this.participants) {
      if (participant.status !== "RACING") continue;
      const obstacles = [...rabbitObstacles];
      if (this.selection.mode === "on") obstacles.push(...this.opponentObstacles(participant));
      participant.readings = readSensors(
        participant.car,
        this.track,
        this.data.perception.ray_angles_degrees,
        this.data.perception.max_range,
        obstacles,
      );
      const version = participant.driver?.observation_version ?? "perception-v1";
      let routeContext: { normalizedOffset: number; headingError: number } | undefined;
      if (version === "perception-v3") {
        const routeProjection = this.track.projectNear(
          participant.car.position, participant.progress.currentSegmentIndex, 3,
        );
        const routeHeading = this.track.pointAt(routeProjection.progress).heading;
        routeContext = {
          normalizedOffset: routeProjection.signedLateralOffset / Math.max(1e-9, routeProjection.allowedHalfWidth),
          headingError: Math.atan2(
            Math.sin(routeHeading - participant.car.heading),
            Math.cos(routeHeading - participant.car.heading),
          ),
        };
      }
      participant.observation = encodeObservation(
        participant.readings,
        clip(participant.car.speed / participant.vehicle.physics.max_forward_speed, -1, 1),
        version,
        routeContext,
      );
    }
    for (const participant of this.participants) {
      if (participant.status !== "RACING") continue;
      const activations = participant.driver ? forward(participant.driver.network, participant.observation) : null;
      const control = activations
        ? { steering: activations.outputs[0] ?? 0, throttleBrake: activations.outputs[1] ?? 0 }
        : humanControl;
      participant.activations = activations;
      participant.control = control;
      const previousProgress = this.track.projectNear(
        participant.car.position, participant.progress.currentSegmentIndex, 3,
      ).progress;
      const activeTornado = this.track.tornadoes.find((tornado) => tornado.id === participant.car.stuntSegmentId);
      let stuntEvent: StuntEvent | null = null;
      if (activeTornado) {
        ({ car: participant.car, event: stuntEvent } = participant.stuntController.updateTornado(participant.car, activeTornado, this.track, control, dt));
      } else if (participant.car.airborne) {
        ({ car: participant.car, event: stuntEvent } = participant.stuntController.updateAirborne(participant.car, control, dt));
      } else {
        this.updateSurfaceGrip(participant);
        participant.car = updateCar(participant.car, control, participant.vehicle.physics, dt);
        stuntEvent = this.updateGroundStunts(participant, previousProgress);
      }
      if (stuntEvent) this.recordStuntEvent(participant, stuntEvent);
      const margin = Math.hypot(participant.vehicle.physics.car_length / 2, participant.vehicle.physics.car_width / 2);
      const validState = [...participant.car.position, participant.car.heading, participant.car.speed, participant.car.z, participant.car.verticalVelocity, participant.car.pitch].every(Number.isFinite);
      const projection = validState
        ? this.track.projectNear(participant.car.position, participant.progress.currentSegmentIndex, 3)
        : null;
      const outsideRecoverableWorld = projection !== null && projection.distanceFromCenter > projection.allowedHalfWidth + 180;
      for (const obstacle of this.track.clearedObstacles(participant.car.position, margin, participant.car.z)) {
        if (participant.clearedObstacleIds.has(obstacle.id)) continue;
        participant.clearedObstacleIds.add(obstacle.id);
        participant.obstacleClears += 1;
        participant.stuntEvents.push({ kind: "obstacle-clear", stuntId: obstacle.id, z: participant.car.z, speed: participant.car.speed, verticalSpeed: participant.car.verticalVelocity, airborneTime: participant.car.airborneTime, jumpDistance: 0 });
      }
      const constrainedTornado = this.track.tornadoes.some((tornado) => tornado.id === participant.car.stuntSegmentId);
      const contacts = validState && !outsideRecoverableWorld && !constrainedTornado
        ? this.track.physicalContacts(participant.car.position, margin, participant.car.z, projection ?? undefined)
        : [];
      const collided = contacts.length > 0;
      if (collided) {
        for (let passIndex = 0; passIndex < 3; passIndex += 1) {
          const active = this.track.physicalContacts(participant.car.position, margin, participant.car.z);
          if (active.length === 0) break;
          const contact = [...active].sort((first, second) => second.penetration - first.penetration)[0];
          if (!contact) break;
          participant.car = resolveWallContact(participant.car, contact, participant.vehicle.physics).car;
        }
      } else {
        participant.car.collided = false;
      }
      const collisionEvent = collided && !participant.wallContactActive;
      participant.wallContactActive = collided;
      if (collisionEvent) participant.trackCollisions += 1;
      if (collisionEvent && this.steps - participant.lastLandingStep <= Math.round(0.6 / dt)) participant.landingCollisions += 1;
      const progress = participant.progress.update(participant.car.position, dt, collided);
      if (stuntEvent?.kind === "landing" && participant.firstLandingProgress === null) {
        participant.firstLandingProgress = progress.totalProgress;
      }
      if (participant.firstLandingProgress !== null) {
        participant.postLandingProgress = Math.max(
          participant.postLandingProgress,
          progress.totalProgress - participant.firstLandingProgress,
        );
      }
      const elapsed = (this.steps + 1) * dt;
      if (participant.driver && validState && progress.secondsWithoutProgress >= 5) {
        this.resetStrandedAi(participant, elapsed, progress.totalProgress);
        continue;
      }
      participant.recovery.update(elapsed, participant.car.position, progress.totalProgress, collided, participant.car.speed);
      // Human inactivity is not a failed AI episode: input must remain available.
      this.recordFinishLineCrossing(participant, progress.totalProgress);
      if (participant.completedLaps >= this.selection.laps) {
        participant.status = "FINISHED";
        participant.finishTime = elapsed;
      } else if (!validState || outsideRecoverableWorld || (participant.driver !== null && participant.recovery.shouldTerminate) || elapsed >= 300) {
        participant.status = "DNF";
        participant.terminationReason = !validState
          ? "invalid-physical-state"
          : outsideRecoverableWorld
            ? "outside-recoverable-world"
            : participant.driver !== null && participant.recovery.shouldTerminate
              ? "stuck-timeout"
              : "race-timeout";
      }
    }
    if (this.selection.mode === "on") this.resolveVehicleContacts();
    this.resolveRabbitContacts();
    this.steps += 1;
    this.updatePodiumClosure();
  }

  private updateFinishedCoast(dt: number): void {
    for (const participant of this.participants) {
      if (participant.status !== "FINISHED" || participant.car.speed <= 0) continue;
      participant.control = finishCoastControl;
      participant.car = updateCar(participant.car, finishCoastControl, participant.vehicle.physics, dt);
      if (participant.car.speed < 0.5) participant.car.speed = 0;
    }
  }

  private updatePodiumClosure(): void {
    const podiumIsFull = this.participants.filter((participant) => participant.status === "FINISHED").length >= 3;
    const humanStillRacing = this.participants.some(
      (participant) => participant.driver === null && participant.status === "RACING",
    );
    if (!podiumIsFull || humanStillRacing) return;
    this.podiumLockedAt ??= this.elapsedSeconds;
    if ((this.podiumGraceRemaining ?? PODIUM_GRACE_SECONDS) > 0) return;
    for (const participant of this.participants) {
      if (participant.status !== "RACING") continue;
      participant.status = "DNF";
      participant.terminationReason = "podium-grace-expired";
    }
  }

  private resetStrandedAi(participant: RaceParticipant, elapsed: number, totalProgress: number): void {
    const projection = this.track.projectNear(
      participant.car.position, participant.progress.currentSegmentIndex, 3,
    );
    const pose = this.track.pointAt(projection.progress);
    participant.car.position = cloneVec(pose.point);
    participant.car.heading = pose.heading;
    participant.car.speed = 0;
    participant.car.collided = false;
    participant.car.z = 0;
    participant.car.verticalVelocity = 0;
    participant.car.airborne = false;
    participant.car.pitch = 0;
    participant.car.stuntSegmentId = null;
    participant.car.stuntProgress = 0;
    participant.car.stuntSpeed = 0;
    participant.car.airborneTime = 0;
    participant.car.landingGripSeconds = 0;
    participant.car.waterGripSeconds = 0;
    participant.car.waterGripFactor = 1;
    participant.car.onWater = false;
    participant.car.onBridge = false;
    participant.progress.reanchor(participant.car.position);
    participant.recovery.reanchor(elapsed, participant.car.position, totalProgress);
    participant.lastRaceProgress = projection.progress;
    participant.lastRaceSegmentIndex = projection.segmentIndex;
    participant.wallContactActive = false;
    participant.automaticResets += 1;
    const slotId = participant.selection.slotId;
    this.activeVehicleContacts = new Set([...this.activeVehicleContacts].filter((key) => !key.split("|").includes(slotId)));
    this.activeRabbitContacts = new Set([...this.activeRabbitContacts].filter((key) => !key.startsWith(`${slotId}|`)));
  }

  private updateGroundStunts(participant: RaceParticipant, previousProgress: number): StuntEvent | null {
    const projection = this.track.projectNear(
      participant.car.position, participant.progress.currentSegmentIndex, 3,
    );
    const progress = projection.progress;
    const wasOnBridge = participant.car.onBridge;
    participant.car.onBridge = false;
    for (const tornado of this.track.tornadoes) {
      if (this.crossed(previousProgress, progress, tornado.start_progress) && Math.abs(projection.signedLateralOffset) <= tornado.lateral_tolerance && participant.car.speed > 0) {
        const result = participant.stuntController.enterTornado(participant.car, tornado);
        participant.car = result.car;
        return result.event;
      }
    }
    let onRamp = false;
    for (const ramp of this.track.ramps) {
      if (ramp.start_progress <= progress && progress <= ramp.end_progress) {
        onRamp = true;
        const fraction = (progress - ramp.start_progress) / Math.max(1e-9, ramp.end_progress - ramp.start_progress);
        participant.car.z = ramp.height * clip(fraction, 0, 1);
        participant.car.pitch = -ramp.angle;
      }
      if (this.crossed(previousProgress, progress, ramp.end_progress) && Math.abs(projection.signedLateralOffset) <= ramp.lateral_tolerance && participant.car.speed >= this.data.game_semantics.stunts.minimum_launch_speed) {
        const result = participant.stuntController.launch(participant.car, ramp);
        participant.car = result.car;
        return result.event;
      }
    }
    let onBridge = false;
    for (const bridge of this.track.bridges) {
      const withinProgress = bridge.start_progress <= progress && progress <= bridge.end_progress;
      const enteredAtStart = previousProgress <= bridge.start_progress && bridge.start_progress <= progress;
      const enteredAtEnd = progress <= bridge.end_progress && bridge.end_progress <= previousProgress;
      const insideEntryGate = Math.abs(projection.signedLateralOffset)
        <= Math.min(
          bridge.approach_half_width - participant.vehicle.physics.car_width * .5 - 1.5,
          this.track.data.mean_width * .38,
        );
      const enteredThroughEnd = insideEntryGate && (enteredAtStart || enteredAtEnd);
      if (withinProgress && (wasOnBridge || enteredThroughEnd)) {
        onBridge = true;
        participant.car.onBridge = true;
        const bridgeProgress = clip(progress, bridge.start_progress, bridge.end_progress);
        if (this.data.game_semantics.environment_default === "physics-v6-bridges") {
          const route = this.track.pointAt(bridgeProgress);
          const normal: [number, number] = [-Math.sin(route.heading), Math.cos(route.heading)];
          const dx = participant.car.position[0] - route.point[0];
          const dy = participant.car.position[1] - route.point[1];
          const lateral = dx * normal[0] + dy * normal[1];
          const centerLimit = Math.max(
            8,
            bridgeRailHalfWidthAt(bridge, bridgeProgress)
              - participant.vehicle.physics.car_width * .5
              - 1.5,
          );
          if (Math.abs(lateral) > centerLimit) {
            const side = lateral >= 0 ? 1 : -1;
            participant.car.position = [
              route.point[0] + normal[0] * side * centerLimit,
              route.point[1] + normal[1] * side * centerLimit,
            ];
            const headingError = Math.atan2(
              Math.sin(participant.car.heading - route.heading),
              Math.cos(participant.car.heading - route.heading),
            );
            const outwardSpeed = participant.car.speed * Math.sin(headingError) * side;
            if (outwardSpeed > 0) {
              const impactRatio = clip(
                outwardSpeed / Math.max(1, participant.vehicle.physics.max_forward_speed),
                0,
                1,
              );
              const headingNudge = Math.min(
                Math.abs(headingError) * .38,
                .10 + impactRatio * .28,
              );
              participant.car.heading -= side * headingNudge;
              participant.car.speed *= .92 - impactRatio * .18;
            }
          }
        }
        participant.car.z = bridgeHeightAt(bridge, bridgeProgress);
        const rampSpan = Math.max(1e-9, bridge.deck_start_progress - bridge.start_progress);
        participant.car.pitch = bridgeProgress < bridge.deck_start_progress
          ? -Math.atan2(bridge.height, rampSpan)
          : bridgeProgress > bridge.deck_end_progress
            ? Math.atan2(bridge.height, rampSpan)
            : 0;
        break;
      }
    }
    if (participant.car.z > 0 && !onRamp && !onBridge) { participant.car.z = 0; participant.car.pitch = 0; }
    return null;
  }

  private updateSurfaceGrip(participant: RaceParticipant): void {
    const projection = this.track.projectNear(
      participant.car.position, participant.progress.currentSegmentIndex, 3,
    );
    participant.car.onWater = false;
    for (const surface of this.track.surfaces) {
      if (surface.start_progress <= projection.progress && projection.progress <= surface.end_progress && Math.abs(projection.signedLateralOffset) <= surface.lateral_tolerance) {
        participant.car.onWater = true;
        const stronger = this.data.game_semantics.environment_default === "physics-v6-bridges";
        participant.car.waterGripSeconds = Math.max(
          participant.car.waterGripSeconds,
          stronger ? Math.max(surface.recovery_seconds, 1.05) : surface.recovery_seconds,
        );
        participant.car.waterGripFactor = Math.min(
          participant.car.waterGripFactor,
          stronger ? Math.min(surface.grip, 0.08) : surface.grip,
        );
        return;
      }
    }
  }

  private crossed(previous: number, currentValue: number, targetValue: number): boolean {
    let current = currentValue;
    let target = targetValue;
    if (current < previous - this.track.length * 0.5) current += this.track.length;
    if (target < previous) target += this.track.length;
    return previous < target && target <= current;
  }

  private recordStuntEvent(participant: RaceParticipant, event: StuntEvent): void {
    participant.stuntEvents.push(event);
    if (event.kind === "takeoff") { participant.rampEntries += 1; participant.airbornePhases += 1; }
    else if (event.kind === "landing") {
      participant.lastLandingStep = this.steps;
      participant.totalAirTime += event.airborneTime;
      participant.totalJumpDistance += event.jumpDistance;
      if (Math.abs(event.verticalSpeed) <= this.data.game_semantics.stunts.clean_landing_vertical_speed) participant.cleanLandings += 1;
    } else if (event.kind === "tornado-entry") participant.tornadoEntries += 1;
    else if (event.kind === "tornado-complete") participant.tornadoCompletions += 1;
    else if (event.kind === "tornado-misfire") participant.tornadoMisfires += 1;
  }

  private opponentObstacles(selected: RaceParticipant): SensorObstacle[] {
    return this.participants
      .filter((participant) => participant !== selected && participant.status === "RACING")
      .map((participant) => ({
        center: cloneVec(participant.car.position),
        radius: collisionRadius(participant.vehicle),
        hitType: "VEHICLE",
        objectId: participant.selection.slotId,
      }));
  }

  private resolveVehicleContacts(): void {
    const contactsNow = new Set<string>();
    const active = this.participants.filter((participant) => participant.status === "RACING");
    for (let passIndex = 0; passIndex < 4; passIndex += 1) {
      for (let firstIndex = 0; firstIndex < active.length; firstIndex += 1) {
        const first = active[firstIndex] as RaceParticipant;
        for (let secondIndex = firstIndex + 1; secondIndex < active.length; secondIndex += 1) {
          const second = active[secondIndex] as RaceParticipant;
          const ids = [first.selection.slotId, second.selection.slotId].sort() as [string, string];
          const key = ids.join("|");
          if (first.car.airborne || second.car.airborne || first.car.stuntSegmentId !== null || second.car.stuntSegmentId !== null || Math.abs(first.car.z - second.car.z) > 12) continue;
          if (!resolveVehicleCollision(first, second, 0.46, 2)) continue;
          contactsNow.add(key);
          if (passIndex === 0 && !this.activeVehicleContacts.has(key)) {
          first.opponentCollisions += 1;
          second.opponentCollisions += 1;
          this.impactEvents.push({
            position: scale(add(first.car.position, second.car.position), 0.5),
            participantIds: ids,
            elapsedSeconds: this.elapsedSeconds,
          });
          if (this.impactEvents.length > 32) this.impactEvents.splice(0, this.impactEvents.length - 32);
          }
        }
      }
    }
    this.activeVehicleContacts = contactsNow;
  }

  private resolveRabbitContacts(): void {
    const contactsNow = new Set<string>();
    for (const participant of this.participants) {
      if (participant.status !== "RACING") continue;
      const radius = collisionRadius(participant.vehicle);
      for (const rabbit of this.rabbitWorld.rabbits) {
        const key = `${participant.selection.slotId}|${rabbit.id}`;
        const delta = sub(participant.car.position, rabbit.position);
        if (dot(delta, delta) > (radius + 6) ** 2) continue;
        contactsNow.add(key);
        if (this.activeRabbitContacts.has(key)) continue;
        participant.rabbitIncidents += 1;
        this.rabbitWorld.addDecal(rabbit);
        const impact = Math.min(1, Math.abs(participant.car.speed) / Math.max(1, participant.car.speed + 90));
        const massFactor = 1200 / participant.vehicle.mass;
        const side = Math.sin(participant.car.heading - rabbit.heading) < 0 ? -1 : 1;
        participant.car.heading += side * (0.08 + 0.18 * impact) * massFactor;
        participant.car.speed *= Math.max(0.42, 1 - 0.20 * massFactor);
        participant.car.position = add(participant.car.position, scale(headingVector(participant.car.heading + side * Math.PI / 2), 2.5 * massFactor));
      }
    }
    this.activeRabbitContacts = contactsNow;
  }

  private recordFinishLineCrossing(participant: RaceParticipant, totalProgress: number): void {
    const projection = this.track.projectNear(
      participant.car.position,
      participant.lastRaceSegmentIndex,
      3,
    );
    const crossed = crossedProgress(
      participant.lastRaceProgress,
      projection.progress,
      0,
      this.track.length,
    );
    participant.lastRaceProgress = projection.progress;
    participant.lastRaceSegmentIndex = projection.segmentIndex;
    const nextLap = participant.completedLaps + 1;
    const finishTolerance = participant.vehicle.physics.max_forward_speed * this.data.game_semantics.fixed_dt + 0.25;
    const onRoad = projection.distanceFromCenter <= projection.allowedHalfWidth + 1e-6;
    if (!officialLapCrossing(
      crossed,
      totalProgress,
      nextLap,
      this.track.length,
      finishTolerance,
      participant.car.speed,
      onRoad,
    )) return;
    participant.completedLaps = nextLap;
    const elapsed = (this.steps + 1) * this.data.game_semantics.fixed_dt;
    while (participant.observedLaps < participant.completedLaps) {
      const lapTime = elapsed - participant.lastLapStarted;
      participant.fastestLap = participant.fastestLap === null ? lapTime : Math.min(participant.fastestLap, lapTime);
      participant.lastLapStarted = elapsed;
      participant.observedLaps += 1;
    }
  }

  get results(): RaceResult[] {
    return rankRaceResults(this.participants
      .map((participant) => {
        const progress = participant.progress.snapshot();
        return {
          participantId: participant.selection.slotId,
          displayName: participant.driver?.name ?? "YOU",
          driverId: participant.selection.driverId,
          vehicleId: participant.selection.vehicleId,
          color: participant.selection.color,
          status: participant.status,
          laps: participant.completedLaps,
          progressLaps: Math.max(0, progress.totalProgress / this.track.length),
          finishTime: participant.finishTime,
          fastestLap: participant.fastestLap,
          trackCollisions: participant.trackCollisions,
          opponentCollisions: participant.opponentCollisions,
          rabbitIncidents: participant.rabbitIncidents,
          stuckEvents: participant.recovery.stuckEvents,
          recoveredEvents: participant.recovery.recoveredEvents,
          reverseActivations: participant.recovery.reverseActivations,
          reverseTime: participant.recovery.reverseTime,
          reverseDistance: participant.recovery.reverseDistance,
          terminationReason: participant.terminationReason,
          rampEntries: participant.rampEntries,
          airbornePhases: participant.airbornePhases,
          cleanLandings: participant.cleanLandings,
          obstacleClears: participant.obstacleClears,
          landingCollisions: participant.landingCollisions,
          tornadoEntries: participant.tornadoEntries,
          tornadoCompletions: participant.tornadoCompletions,
          tornadoMisfires: participant.tornadoMisfires,
          airTime: participant.totalAirTime,
          jumpDistance: participant.totalJumpDistance,
          postLandingProgress: participant.postLandingProgress,
        } satisfies RaceResult;
      }));
  }
}

export function crossedProgress(previous: number, currentValue: number, targetValue: number, trackLength: number): boolean {
  let current = currentValue;
  let target = targetValue;
  if (current < previous - trackLength * 0.5) current += trackLength;
  if (target <= previous) target += trackLength;
  return previous < target && target <= current;
}

export function officialLapCrossing(
  crossed: boolean,
  totalProgress: number,
  nextLap: number,
  trackLength: number,
  tolerance: number,
  speed: number,
  onRoad: boolean,
): boolean {
  return crossed
    && totalProgress >= nextLap * trackLength - tolerance
    && speed > 0
    && onRoad;
}
